import React, { useState } from 'react';
import { 
  FiUsers, 
  FiCalendar, 
  FiEye, 
  FiEdit3, 
  FiTrash2,
  FiCheck,
  FiX,
  FiSearch,
  FiFilter,
  FiDownload,
  FiPlus
} from 'react-icons/fi';
import BulkActionsBar from './BulkActionsBar';
import AdvancedFilter from './AdvancedFilter';

export default function OrganizationTable({ 
  organizations = [], 
  loading = false,
  onView,
  onEdit,
  onDelete,
  onBulkAction,
  pagination,
  onPageChange,
  searchTerm,
  onSearchChange,
  filters = {},
  onFiltersChange
}) {
  const [selectedOrgs, setSelectedOrgs] = useState(new Set());
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrgs(new Set(organizations.map(org => org.organization_id)));
    } else {
      setSelectedOrgs(new Set());
    }
  };

  const handleSelectOrg = (orgId) => {
    const newSelected = new Set(selectedOrgs);
    if (newSelected.has(orgId)) {
      newSelected.delete(orgId);
    } else {
      newSelected.add(orgId);
    }
    setSelectedOrgs(newSelected);
  };

  const handleBulkAction = async (action) => {
    if (selectedOrgs.size === 0) return;
    
    await onBulkAction(action, Array.from(selectedOrgs));
    setSelectedOrgs(new Set());
  };

  const getStatusBadge = (org) => {
    if (!org.is_active) {
      return (
        <span style={{
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: '500',
          backgroundColor: '#FEE2E2',
          color: '#991B1B'
        }}>
          Inactive
        </span>
      );
    }

    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '500',
        backgroundColor: '#D1FAE5',
        color: '#065F46'
      }}>
        Active
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      'business': { bg: '#FEF3C7', color: '#92400E' },
      'education': { bg: '#E0E7FF', color: '#3730A3' },
      'government': { bg: '#F0FDF4', color: '#166534' },
      'nonprofit': { bg: '#FDF2F8', color: '#9D174D' }
    };

    const colorScheme = colors[type] || colors['business'];

    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '500',
        backgroundColor: colorScheme.bg,
        color: colorScheme.color
      }}>
        {type || 'Business'}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E2E8F0'
      }}>
        <div style={{ textAlign: 'center', color: '#6B7280' }}>
          Loading organizations...
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E2E8F0',
        overflow: 'hidden'
      }}>
        {/* Header with Search and Actions */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h3 style={{ margin: 0, color: '#1F2937', fontSize: '1.25rem', fontWeight: '600' }}>
            Organizations ({organizations.length})
          </h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', position: 'relative' }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <FiSearch style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6B7280',
                fontSize: '16px'
              }} />
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                style={{
                  paddingLeft: '2.5rem',
                  padding: '0.75rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  width: '250px'
                }}
              />
            </div>

            {/* Advanced Filter */}
            <div style={{ position: 'relative' }}>
              <AdvancedFilter
                isOpen={showAdvancedFilter}
                onToggle={() => setShowAdvancedFilter(!showAdvancedFilter)}
                onApply={onFiltersChange}
                onClear={() => onFiltersChange({})}
                filters={filters}
                entityType="organizations"
              />
            </div>

            {/* Export Button */}
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                backgroundColor: '#F9FAFB',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '0.875rem',
                cursor: 'pointer',
                color: '#374151'
              }}
              onClick={() => {
                // TODO: Implement export
                console.log('Export organizations');
              }}
            >
              <FiDownload size={16} />
              Export
            </button>

            {/* Add Organization Button */}
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
              onClick={() => {
                // TODO: Open add organization modal
                console.log('Add new organization');
              }}
            >
              <FiPlus size={16} />
              Add Organization
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB' }}>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151',
                  width: '40px'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedOrgs.size === organizations.length && organizations.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Organization
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Type & Status
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Members
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Created
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'right', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org) => (
                <tr 
                  key={org.organization_id} 
                  style={{ 
                    borderBottom: '1px solid #F1F5F9',
                    backgroundColor: selectedOrgs.has(org.organization_id) ? '#FEF3C7' : 'white'
                  }}
                >
                  <td style={{ padding: '1rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedOrgs.has(org.organization_id)}
                      onChange={() => handleSelectOrg(org.organization_id)}
                    />
                  </td>
                  
                  <td style={{ padding: '1rem' }}>
                    <div>
                      <div style={{ fontWeight: '500', color: '#1F2937' }}>
                        {org.organization_name}
                      </div>
                      {org.domain && (
                        <div style={{ fontSize: '0.875rem', color: '#6B7280', marginTop: '0.25rem' }}>
                          {org.domain}
                        </div>
                      )}
                      {org.contact_email && (
                        <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
                          {org.contact_email}
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {getTypeBadge(org.organization_type)}
                      {getStatusBadge(org)}
                    </div>
                  </td>
                  
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FiUsers size={14} style={{ color: '#6B7280' }} />
                      <span style={{ fontSize: '0.875rem', color: '#1F2937', fontWeight: '500' }}>
                        {org.member_count || 0}
                      </span>
                    </div>
                    {org.admin_count > 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                        {org.admin_count} admin{org.admin_count > 1 ? 's' : ''}
                      </div>
                    )}
                  </td>
                  
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FiCalendar size={14} style={{ color: '#6B7280' }} />
                      <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                        {org.created_at ? new Date(org.created_at).toLocaleDateString() : 'Unknown'}
                      </span>
                    </div>
                  </td>
                  
                  <td style={{ padding: '1rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <button
                        onClick={() => onView(org.organization_id)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#6B7280'
                        }}
                        title="View Organization"
                      >
                        <FiEye size={14} />
                      </button>
                      
                      <button
                        onClick={() => onEdit(org)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#6B7280'
                        }}
                        title="Edit Organization"
                      >
                        <FiEdit3 size={14} />
                      </button>
                      
                      <button
                        onClick={() => onDelete(org.organization_id)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #FCA5A5',
                          borderRadius: '6px',
                          backgroundColor: '#FEF2F2',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#EF4444'
                        }}
                        title="Delete Organization"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </span>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  color: pagination.page === 1 ? '#9CA3AF' : '#374151'
                }}
              >
                Previous
              </button>
              
              <span style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                color: '#6B7280',
                display: 'flex',
                alignItems: 'center'
              }}>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  color: pagination.page === pagination.totalPages ? '#9CA3AF' : '#374151'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {organizations.length === 0 && (
          <div style={{
            padding: '4rem',
            textAlign: 'center',
            color: '#6B7280'
          }}>
            No organizations found
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedOrgs.size}
        entityType="organizations"
        onAction={handleBulkAction}
        onClearSelection={() => setSelectedOrgs(new Set())}
      />
    </div>
  );
}